import { useTranslation } from "@msuite/ui/localization";
import { useMemo } from "react";
import { useCrewDefinitionAbsenceDays } from "@/hooks/use-crew-definition-absence-days";
import type { CrewDefinitionList } from "@/hooks/use-crew-definitions";
import {
	CapacityCalendarRowWrapper,
	spanReachesWindow,
	useCapacityCalendarContext,
} from "../capacity-calendar";
import { CapacityPlannerCrewRow } from "./capacity-planner-crew-row";

/** Props Interface */
interface CapacityPlannerCrewRowsProps {
	crewDefinitions: CrewDefinitionList;
}

/** No crew has an absence until the answer arrives, so a shared empty set stands in for one. */
const NO_ABSENCES: ReadonlySet<string> = new Set();

/**
 * The standing crews the visible window can see. A crew is listed when its span overlaps the three
 * weeks on screen, so moving the week picker changes which crews the board carries.
 *
 * Both the filter and the absence window run here rather than in the view because the window lives
 * in the calendar's context, and a view's children are created outside the provider — only a
 * component rendered *inside* `CapacityCalendar` can read which days are on screen.
 */
export function CapacityPlannerCrewRows({ crewDefinitions }: CapacityPlannerCrewRowsProps) {
	/** Context */
	const { controller } = useCapacityCalendarContext();

	/** Hooks */
	const { t } = useTranslation();

	/** Computed */
	const { windowStart, windowEnd } = controller;

	/** Query */
	const { absenceDays } = useCrewDefinitionAbsenceDays(windowStart, windowEnd);

	/** Computed */
	const visible = useMemo(() => {
		const result: CrewDefinitionList = [];
		for (const crewDefinition of crewDefinitions) {
			if (!spanReachesWindow(crewDefinition, windowStart, windowEnd)) continue;
			result.push(crewDefinition);
		}
		return result;
	}, [crewDefinitions, windowStart, windowEnd]);

	const absenceDatesByCrew = useMemo(() => {
		const byCrew = new Map<string, Set<string>>();
		for (const day of absenceDays) {
			const existing = byCrew.get(day.crewDefinitionId);
			if (existing) existing.add(day.date);
			else byCrew.set(day.crewDefinitionId, new Set([day.date]));
		}
		return byCrew;
	}, [absenceDays]);

	/** Render */
	if (!visible.length) {
		return (
			<CapacityCalendarRowWrapper variant="sub">
				<div className="min-w-0 col-span-full">
					<p className="label truncate">{t("planning.crews.no_crews_in_window")}</p>
				</div>
			</CapacityCalendarRowWrapper>
		);
	}

	return visible.map((crewDefinition) => (
		<CapacityPlannerCrewRow
			key={crewDefinition.id}
			crewDefinition={crewDefinition}
			absenceDates={absenceDatesByCrew.get(crewDefinition.id) ?? NO_ABSENCES}
		/>
	));
}
